import React, { ReactElement, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  clearCourseAnccCreateStatus,
  createCourseV2ANCCAccreditation,
  fetchCourseV2ANCCAccreditation,
  selectCourseV2ANCCCreateStatusByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import {
  ANCC_TARGET_AUDIENCES,
  ANCC_EDUCATIONAL_NEEDS_OPTIONS,
} from '../constants'

type Props = {
  open: boolean
  onClose: () => void
  courseID: string
  courseName?: string
}

export default ({
  open,
  onClose,
  courseID,
  courseName,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()

  let createStatus = useAppSelector(
    selectCourseV2ANCCCreateStatusByCourseID(courseID),
  )
  let loading = createStatus === 'pending'

  let form = useForm({
    defaultValues: {
      courseCode: '',
      expirationDate: '',
      targetAudience: [] as string[],
      educationalNeeds: [] as string[],
      competency: '',
      professionalSource: '',
    },
    onSubmit: ({ value }) => {
      dispatch(
        createCourseV2ANCCAccreditation({
          courseID,
          courseCode: value.courseCode,
          expirationDate: value.expirationDate
            ? new Date(value.expirationDate).toISOString()
            : '',
          targetAudience: value.targetAudience,
          educationalNeeds: value.educationalNeeds,
          competency: value.competency || null,
          professionalSource: value.professionalSource || null,
        }),
      )
    },
  })

  useEffect(() => {
    if (createStatus === 'fulfilled') {
      dispatch(fetchCourseV2ANCCAccreditation(courseID))
      dispatch(clearCourseAnccCreateStatus(courseID))
      form.reset()
      onClose()
    }
    if (createStatus === 'rejected') {
      dispatch(clearCourseAnccCreateStatus(courseID))
    }
  }, [createStatus, courseID, dispatch, onClose])

  useEffect(() => {
    if (open) {
      let twoYears = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0]
      form.setFieldValue('expirationDate', twoYears)
    }
  }, [open])

  let handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create ANCC Accreditation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          <Typography color="text.secondary" variant="body2">
            Create ANCC accreditation for {courseName || 'this course'}. Course
            code, expiration date, target audience, and educational needs are
            required for compliance approval.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => (
              <TextField
                label="Course Code"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
                fullWidth
                sx={modalInputSx}
                placeholder="e.g., ANCC12345"
              />
            )}
          />

          <form.Field
            name="expirationDate"
            children={(field) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiration Date"
                  value={field.state.value ? dayjs(field.state.value) : null}
                  onChange={(val) =>
                    field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                  }
                  onClose={field.handleBlur}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: modalInputSx,
                      onBlur: field.handleBlur,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name={'targetAudience' as any}
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Target Audience</InputLabel>
                  <Select
                    multiple
                    value={field.state.value}
                    label="Target Audience"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                    input={<OutlinedInput label="Target Audience" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.isArray(selected) &&
                          selected.map((v) => (
                            <Chip key={v} label={v} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {ANCC_TARGET_AUDIENCES.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <form.Field
              name={'educationalNeeds' as any}
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Educational Needs</InputLabel>
                  <Select
                    multiple
                    value={field.state.value}
                    label="Educational Needs"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                    input={<OutlinedInput label="Educational Needs" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.isArray(selected) &&
                          selected.map((v) => (
                            <Chip key={v} label={v} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {ANCC_EDUCATIONAL_NEEDS_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <form.Field
            name="competency"
            children={(field) => (
              <TextField
                multiline
                minRows={3}
                label="Competency (Optional)"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                fullWidth
                sx={modalInputSx}
                placeholder="Clinical competency standards"
              />
            )}
          />

          <form.Field
            name="professionalSource"
            children={(field) => (
              <TextField
                multiline
                minRows={3}
                label="Professional Source (Optional)"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                fullWidth
                sx={modalInputSx}
                placeholder="American Nurses Association or other professional source"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create ANCC Accreditation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
